<template>
  <div class="w-full min-w-full max-h-[calc(100vh_-_100px)] overflow-hidden">
    <div class="container max-w-[1440px] min-h-[calc(100vh_-_100px)] mx-auto">
      <div class="flex min-h-[calc(100vh_-_100px)]">
        <div
          v-on-click-outside="hideHistoryTray"
          class="absolute md:relative md:ml-0 w-[280px] px-5 py-10 shrink-0 z-10 min-h-[calc(100vh_-_100px)] transition-all duration-150 ease-linear"
          :class="{
            '-ml-[280px]': !historyTrayOpen,
            'ml-0': historyTrayOpen,
            'md:ml-[-280px] opacity-0': !hasHistory,
          }"
        >
          <div
            class="rotate-90 origin-bottom-left absolute left-[280px] bg-tiara-400 text-white px-3 pt-1 pb-[1px] rounded-t uppercase top-0 md:hidden cursor-pointer"
            @click="toggleHistoryTray()"
          >
            History
          </div>
          <chat-history
            ref="chatHistory"
            :expire-anonymous-days="expireAnonymousDays"
            :usage="usage"
            @toggle-history-tray="toggleHistoryTray"
            @set-has-history="setHasHistory"
          />
        </div>
        <div class="w-full relative transition-all duration-150">
          <compass-background :z-index="0" />
          <div
            class="px-10 w-full overflow-auto flex flex-col"
            :class="{ 'justify-center': !researchCompass?.questions?.length }"
            :style="{ height: `calc(100vh - ${followupHeight + 100}px)` }"
          >
            <template v-if="loadingResearchCompass">
              <div
                class="w-full text-center flex h-[200px] items-center justify-center"
              >
                <span class="text-3xl text-tiara-300"
                  ><i class="fas fa-circle-notch fa-spin"></i
                ></span>
              </div>
            </template>
            <template v-else>
              <div
                v-if="!Object.keys(researchCompass).length"
                class="flex flex-col justify-center items-center mb-5 mt-0"
              >
                <h1 id="headline-1">
                  <span v-if="!Object.keys(researchCompass).length"
                    >Start with a question</span
                  >
                </h1>
                <h4
                  id="headline-2"
                  class="text-center font-headline text-xl text-blueDianne/50 my-5"
                >
                  <span
                    >Get cited answers and ranked expertise from 220+ million
                    research papers.</span
                  >
                </h4>
              </div>
              <div v-else class="center-holder flex flex-col">
                <!-- <expiry-banner
                  :research-compass="researchCompass"
                  :expire-anonymous-days="expireAnonymousDays"
                  @adopt="adopt"
                /> -->
                <h1
                  class="text-blueDianne dark:text-white/80 text-3xl font-headline mb-3 mt-8"
                >
                  <span v-if="isEditingTitle">
                    <input
                      ref="newTitle"
                      v-model="newResearchCompassTitle"
                      class="p-1 -m-1 inline text-3xl"
                      style="field-sizing: content"
                      @keyup.enter="editResearchCompassTitle"
                      @blur="editResearchCompassTitle"
                    />
                  </span>
                  <span v-else
                    ><div class="relative p-1 -m-1 inline">
                      {{ researchCompass.title }}
                    </div>
                    <span class="text-xs ml-3 cursor-pointer"
                      ><i
                        class="fa-solid fa-pencil"
                        @click="editTitle()"
                      ></i></span
                  ></span>
                </h1>
              </div>
              <div class="flex flex-wrap -mx-10 md:mx-0">
                <template v-if="!researchCompass?.questions?.length">
                  <div class="center-holder">
                    <div
                      class="relative flex flex-col min-w-0 break-words w-full mb-0 md:mb-6 rounded-lg z-[1]"
                    >
                      <search-box
                        ref="initialInput"
                        :modes="modes"
                        :model="model"
                        placeholder="What would you like to learn more about?"
                        :filters="filters"
                        :remaining-chat-count="usage?.remainingChats"
                        @toggle-ideation-modal="toggleIdeationModal"
                        @toggle-mode="toggleMode"
                        @update-model="updateModel"
                        @create-research-compass-question="
                          createResearchCompassQuestion
                        "
                        @save-years="saveYears"
                        @save-quartile="saveQuartile"
                        @save-citation-count="saveCitationCount"
                        @save-influential-citation-count="
                          saveInfluentialCitationCount
                        "
                        @save-open-access="saveOpenAccessOnly"
                      />
                    </div>
                    <div class="flex mt-5 -mx-2 relative z-0">
                      <div
                        v-for="example in examples"
                        :key="example"
                        class="bg-white/30 dark:bg-white/10 rounded-2xl cursor-pointer w-1/3 mx-2 border border-tiara/30 dark:border-0 px-5 py-3 font-headline flex flex-col justify-between relative z-10 dark:text-white/80"
                        @click="selectSuggestion(example)"
                      >
                        <p>{{ example }}</p>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div
                    v-for="(
                      question, questionIndex
                    ) in researchCompass.questions"
                    :key="`question${questionIndex}`"
                    ref="question"
                    class="center-holder w-full py-5 px-10 md:px-0"
                  >
                    <div class="w-full mx-auto">
                      <chat-question-card
                        :research-compass="researchCompass"
                        :question="question"
                        :model="model"
                        :pdf-base-url="pdfBaseUrl"
                        @reload-research-compass="loadResearchCompass"
                        @reload-followup-questions="getFollowupQuestions"
                      />
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </div>
          <div
            v-if="researchCompass?.questions?.length > 0"
            :style="{ height: `${followupHeight}px` }"
            class="absolute bottom-0 left-1/2 -translate-x-1/2 w-[calc(100%_-_40px)] max-w-[900px] border-t border-x border-gray-300 dark:border-gray-800 py-3 bg-white/80 dark:bg-[#0d1a20]/80 backdrop-blur-xl z-20 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.1)] dark:shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.5)] transition-all duration-300 flex items-center rounded-t-2xl"
          >
            <div class="w-full mx-auto px-4">
              <!-- <div
                class="absolute bg-puertoRico text-blueDianne text-xs font-bold uppercase z-10 top-0 right-0 px-1"
              >
                <context-popper :text="remainingQuestionCountText"
                  ><span
                    ><span class="uppercase text-blueDianne"
                      >{{ remainingQuestionCount }} followup<span
                        v-if="remainingQuestionCount !== 1"
                        >s</span
                      >
                      remaining</span
                    >
                    &nbsp;<i class="fa-solid fa-circle-info"></i
                  ></span>
                </context-popper>
              </div> -->
              <search-box
                ref="followupInput"
                autocomplete-direction="up"
                :modes="modes"
                :model="model"
                placeholder="Ask a followup question."
                :followup-questions="followupQuestions"
                :loading-followup-questions="loadingFollowupQuestions"
                :remaining-question-count="remainingQuestionCount"
                :filters="filters"
                @toggle-ideation-modal="toggleIdeationModal"
                @toggle-mode="toggleMode"
                @update-model="updateModel"
                @create-research-compass-question="
                  createResearchCompassQuestion
                "
                @change-height="changeFollowupHeight"
                @save-years="saveYears"
                @save-quartile="saveQuartile"
                @save-citation-count="saveCitationCount"
                @save-influential-citation-count="saveInfluentialCitationCount"
                @save-open-access="saveOpenAccessOnly"
              />
            </div>
          </div>
        </div>
      </div>
      <right-side-modal
        ref="ideationModal"
        :showing="false"
        :hide-header="true"
        :hide-footer="true"
      >
        <template #body>
          <ideation
            @reload-research-compass="loadResearchCompass"
            @close-ideation-modal="closeIdeationModal"
          />
        </template>
      </right-side-modal>
    </div>
  </div>
</template>

<script>
import { utils, components } from "common-frontend";
import { nextTick } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import { gsap } from "gsap";
import RightSideModal from "../components/Modals/RightSide.vue";

const {
  SearchBox,
  ChatQuestionCard,
  Ideation,
  ChatHistory,
  CompassBackground,
} = components.ResearchCompass;
// import ExpiryBanner from "../components/ResearchCompass/ExpiryBanner.vue";

const { keysToCamel, keysToSnake, loadEnums } = utils;
const timeline = gsap.timeline();

export default {
  components: {
    SearchBox,
    ChatQuestionCard,
    RightSideModal,
    Ideation,
    ChatHistory,
    CompassBackground,
    // ExpiryBanner,
  },
  directives: {
    onClickOutside: vOnClickOutside,
  },
  data() {
    return {
      enums: {},
      loadingResearchCompass: false,
      researchCompass: {},
      researchCompassChannel: null,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      followupHeight: 80,
      newResearchCompassTitle: "",
      isEditingTitle: false,
      followupQuestions: [],
      loadingFollowupQuestions: false,
      examples: [
        "What is the effect of caffeine on honey bee foraging?",
        "What car colors are best for hot climates?",
        "How does the CRISPR-Cas9 system identify specific DNA sequences?",
      ],
      filters: {
        minYear: null,
        maxYear: null,
        minQuartile: null,
        minInfluentialCitationCount: null,
        minCitationCount: null,
        openAccessOnly: null,
      },
      modes: {
        papers: true,
        experts: false,
      },
      model: "gemini_3_5_flash_lite",
      historyTrayOpen: false,
      expireAnonymousDays: 30,
      usage: {},
      followupQuestionsPerChat: 2,
      hasHistory: false,
    };
  },
  computed: {
    pdfBaseUrl() {
      return `${this.apiBaseUrl}/research-compass/${this.$route.params.researchCompassId}`;
    },
    remainingQuestionCountText() {
      return `You can ask ${this.remainingQuestionCount} ${this.researchCompass.questions.length > 1 ? "more" : ""} followup question${this.remainingQuestionCount !== 1 ? "s" : ""} in this chat. Upgrade to get additional followup questions.`;
    },
    remainingQuestionCount() {
      return Math.max(
        0,
        this.followupQuestionsPerChat -
          this.researchCompass.questions.length +
          1,
      );
    },
  },
  watch: {
    $route(newRoute) {
      this.setUpLimits();
      if (newRoute.params?.researchCompassId) {
        this.loadingResearchCompass = true;
        this.followupQuestions = [];
        this.loadResearchCompass();
        this.setUpChannel(newRoute.params.researchCompassId);
      } else {
        this.researchCompass = {};
        this.followupHeight = 0;
        nextTick(() => {
          if (this.$refs.initialInput) {
            this.$refs.initialInput.focusInput();
          }
        });
      }
    },
  },
  mounted() {
    loadEnums(this, {
      SubscriptionLevel: "subscriptionLevelOptions",
    }).then(() => {
      this.setUpLimits();
    });
    if (this.$route.query.access === "notedsourcetest") {
      this.$cookies.set("notedSourceTest", "true", {
        path: "/",
        maxAge: 60 * 60 * 24 * 365,
      });
      window.location.href = this.$route.path;
    }
    if (this.$route.params.researchCompassId) {
      this.loadingResearchCompass = true;
      this.loadResearchCompass();
      this.setUpChannel(this.$route.params.researchCompassId);
    } else {
      if (this.$refs.initialInput) {
        this.$refs.initialInput.focusInput();
      }
      this.followupHeight = 0;
    }
    this.$posthog.capture("ResearchCompassPublic");
    timeline.fromTo(
      "#headline-1",
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
      0,
    );
    timeline.fromTo(
      "#headline-2",
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
      "+=0",
    );
  },
  methods: {
    toggleIdeationModal() {
      this.$refs.ideationModal.toggleModal();
    },
    toggleHistoryTray() {
      this.historyTrayOpen = !this.historyTrayOpen;
    },
    hideHistoryTray() {
      this.historyTrayOpen = false;
    },
    setHasHistory(val) {
      this.hasHistory = val;
    },
    setUpChannel(hashedId) {
      if (this.researchCompassChannel === null) {
        const channel = this.$ably.channels.get(
          `${
            import.meta.env.VITE_ENV_CONFIG_NAMESPACE
          }researchcompass${hashedId}`,
        );
        const self = this;
        channel.subscribe((message) => {
          if (message.name === "UPDATE_CONTENT") {
            self.loadResearchCompass();
          }
        });
        this.researchCompassChannel = channel;
      }
    },
    setUpLimits() {
      if (this.$store.getters.person?.hashedId) {
        this.$api.attemptAccessTokenRefresh(true).then(() => {
          this.$api.axios.get(`/research-compass/usage/`).then((resp) => {
            this.usage = keysToCamel(resp.data);
          });
          if (this.$store.getters.person.subscriptionLevel) {
            const level = this.enums.subscriptionLevelOptions.find(
              (l) =>
                l.value === this.$store.getters.person.subscriptionLevel.value,
            );
            this.followupQuestionsPerChat = level.followupQuestionsPerChat;
          }
        });
      } else {
        // get or set the rcId cookie in addition to getting usage
        this.$api.axiosPublicWithCookies
          .get(`/research-compass/usage/`)
          .then((resp) => {
            this.usage = keysToCamel(resp.data);
          });
      }
    },
    loadIdeationIfAppropriate() {
      if (
        this.researchCompass?.questions?.length === 0 &&
        this.researchCompass?.suggestedConcepts?.length > 0
      ) {
        this.toggleIdeationModal();
      }
    },
    closeIdeationModal(data) {
      this.$refs.ideationModal.closeModal();
      if (data.viewKey) {
        this.$router.push({
          name: "PublicResearchCompassDetail",
          params: {
            researchCompassId: data.hashedId,
            viewKey: data.viewKey,
          },
        });
      } else {
        this.$router.push({
          name: "OwnResearchCompassDetail",
          params: {
            researchCompassId: data.hashedId,
          },
        });
      }
    },
    toggleMode(mode) {
      this.modes[mode] = !this.modes[mode];
    },
    updateModel(model) {
      this.model = model;
    },
    changeFollowupHeight(newHeight) {
      this.followupHeight = newHeight + 25;
    },
    editTitle() {
      this.isEditingTitle = true;
      nextTick(() => {
        this.$refs.newTitle.select();
      });
    },
    saveYears(minYear, maxYear) {
      this.filters.minYear = minYear;
      this.filters.maxYear = maxYear;
    },
    saveQuartile(minQuartile) {
      this.filters.minQuartile = minQuartile;
    },
    saveCitationCount(minCitationCount) {
      this.filters.minCitationCount = minCitationCount;
    },
    saveInfluentialCitationCount(minInfluentialCitationCount) {
      this.filters.minInfluentialCitationCount = minInfluentialCitationCount;
    },
    saveOpenAccessOnly(openAccessOnly) {
      this.filters.openAccessOnly = openAccessOnly;
    },
    loadResearchCompass(scrollToBottom = false) {
      let url = "";
      let api = null;
      if (this.$route.params.viewKey) {
        url = `/research-compass/${this.$route.params.researchCompassId}/?view_key=${this.$route.params.viewKey}`;
        api = this.$api.axiosPublic;
      } else {
        url = `/research-compass/${this.$route.params.researchCompassId}/`;
        api = this.$api.axios;
      }
      if (this.$route.params.researchCompassId) {
        api.get(url).then((resp) => {
          const data = keysToCamel(resp.data);
          if (
            this.$route.params.viewKey &&
            data.person?.hashedId === this.$store.getters.person?.hashedId
          ) {
            this.$router.push({
              name: "OwnResearchCompassDetail",
              params: {
                researchCompassId: data.hashedId,
              },
            });
            return;
          }
          this.researchCompass = data;
          this.loadingResearchCompass = false;
          this.newResearchCompassTitle = data.title;
          if (!this.followupQuestions || this.followupQuestions.length === 0) {
            this.getFollowupQuestions();
          }
          if (scrollToBottom) {
            nextTick(() => {
              const questionElements = this.$refs.question;
              if (questionElements && questionElements.length > 0) {
                const lastQuestionElement =
                  questionElements[questionElements.length - 1];
                lastQuestionElement.scrollIntoView({ behavior: "smooth" });
              }
            });
          }
          if (this.researchCompass.questions?.length > 0) {
            this.followupHeight = 80;
          } else {
            this.followupHeight = 0;
          }
          this.loadIdeationIfAppropriate();
          if (
            data.viewKey &&
            data.person?.hashedId !== this.$store.getters.person?.hashedId
          ) {
            this.saveResearchCompassToCookies(
              data.hashedId,
              data.viewKey,
              data.title,
              false,
            );
          }
        });
      } else {
        this.loadingResearchCompass = false;
        this.researchCompass = {};
        this.followupHeight = 0;
        this.followupQuestions = [];
      }
    },
    getFollowupQuestions() {
      let url = "";
      let api = null;
      if (this.$route.params.viewKey) {
        url = `/research-compass/${this.$route.params.researchCompassId}/followup-questions/?view_key=${this.$route.params.viewKey}`;
        api = this.$api.axiosPublic;
      } else {
        url = `/research-compass/${this.$route.params.researchCompassId}/followup-questions/`;
        api = this.$api.axios;
      }
      if (this.$route.params.researchCompassId) {
        this.loadingFollowupQuestions = true;
        return api
          .post(url)
          .then((resp) => {
            const data = keysToCamel(resp.data);
            this.followupQuestions = data.questions;
          })
          .finally(() => {
            this.loadingFollowupQuestions = false;
          });
      }
      return Promise.resolve();
    },
    createResearchCompass(title, callback) {
      const url = "/research-compass/";
      let api = null;
      if (this.$store.getters.person?.hashedId) {
        api = this.$api.axios;
      } else {
        api = this.$api.axiosPublicWithCookies;
      }
      api
        .post(
          url,
          keysToSnake({
            title,
            conceptOrQuestion: "QUESTION",
          }),
        )
        .then((resp) => {
          const data = keysToCamel(resp.data);
          if (data.viewKey) {
            this.saveResearchCompassToCookies(
              data.hashedId,
              data.viewKey,
              data.title,
              true,
            );
            this.$router.push({
              name: "PublicResearchCompassDetail",
              params: {
                researchCompassId: data.hashedId,
                viewKey: data.viewKey,
              },
            });
          } else {
            this.$refs.chatHistory.getOwnResearchCompassItems();
            this.$router.push({
              name: "OwnResearchCompassDetail",
              params: {
                researchCompassId: data.hashedId,
              },
            });
          }
          this.setUpLimits();
          callback(data);
        })
        .catch((e) => {
          if (this.$refs.initialInput) {
            this.$refs.initialInput.resetLoading();
          }
          if (this.$refs.followupInput) {
            this.$refs.followupInput.resetLoading();
          }
          if (!this.$store.getters.person?.hashedId) {
            // axiosPublic is configured to hide error toasts. This shows it if the user is not logged in.
            this.$toast.error(
              e.response?.data?.message ||
                "There was an error creating your Research Compass. Please try again.",
            );
          }
        });
    },
    createResearchCompassQuestion(data) {
      const self = this;
      const callback = function (researchCompass) {
        if (self.$refs.initialInput)
          self.$refs.initialInput.setLoadingMessage("Searching literature...");
        if (self.$refs.followupInput)
          self.$refs.followupInput.setLoadingMessage("Searching literature...");
        let url = "";
        let api = null;
        if (researchCompass.viewKey) {
          url = `/research-compass/${researchCompass.hashedId}/question/?view_key=${researchCompass.viewKey}`;
          api = self.$api.axiosPublic;
        } else {
          url = `/research-compass/${researchCompass.hashedId}/question/`;
          api = self.$api.axios;
        }
        api
          .post(
            url,
            keysToSnake({
              question: data.question,
              minYear: self.filters.minYear,
              maxYear: self.filters.maxYear,
              minQuartile: self.filters.minQuartile,
              minCitationCount: self.filters.minCitationCount,
              minInfluentialCitationCount:
                self.filters.minInfluentialCitationCount,
              openAccessOnly: self.filters.openAccessOnly,
              maxDocCount: data.maxDocCount,
              upstreamResearchCompassQuestionHashedId:
                self.researchCompass?.questions?.map((question) => {
                  return question.hashedId;
                })[self.researchCompass.questions.length - 1],
              modes: self.modes,
              answerModel: self.model || null,
            }),
          )
          .then(() => {
            self.loadResearchCompass(true);
            self.getFollowupQuestions();
            if (self.$refs.initialInput) {
              self.$refs.initialInput.resetLoading(true);
            }
            if (self.$refs.followupInput) {
              self.$refs.followupInput.resetLoading(true);
            }
            if (window.twq) {
              window.twq("event", "tw-qocm6-qocm6", {});
            }
          })
          .catch((e) => {
            if (self.$refs.initialInput) {
              self.$refs.initialInput.resetLoading();
            }
            if (self.$refs.followupInput) {
              self.$refs.followupInput.resetLoading();
            }
            if (researchCompass.viewKey) {
              // axiosPublic is configured to hide error toasts. This shows it if the user is not logged in.
              self.$toast.error(
                e.response?.data?.message ||
                  "There was an error creating your Research Compass. Please try again.",
              );
            }
          });
      };
      if (!this.researchCompass.hashedId) {
        if (self.$refs.initialInput)
          self.$refs.initialInput.setLoadingMessage("Creating title...");
        if (self.$refs.followupInput)
          self.$refs.followupInput.setLoadingMessage("Creating title...");
        this.getNameForQuestion(data.question).then((name) => {
          if (self.$refs.initialInput)
            self.$refs.initialInput.setLoadingMessage(
              "Saving new Research Compass...",
            );
          if (self.$refs.followupInput)
            self.$refs.followupInput.setLoadingMessage(
              "Saving new Research Compass...",
            );
          this.createResearchCompass(name, callback);
        });
      } else {
        callback(this.researchCompass);
      }
    },
    editResearchCompassTitle() {
      let url = "";
      let api = null;
      if (this.$route.params.viewKey) {
        url = `/research-compass/${this.$route.params.researchCompassId}/?view_key=${this.$route.params.viewKey}`;
        api = this.$api.axiosPublic;
      } else {
        url = `/research-compass/${this.$route.params.researchCompassId}/`;
        api = this.$api.axios;
      }
      if (
        this.newResearchCompassTitle !== this.researchCompass.title &&
        this.newResearchCompassTitle.trim() !== ""
      ) {
        api
          .put(
            url,
            keysToSnake({
              title: this.newResearchCompassTitle,
            }),
          )
          .then((resp) => {
            const data = keysToCamel(resp.data);
            this.researchCompass = data;
            this.isEditingTitle = false;
            if (this.$route.params.viewKey) {
              this.saveResearchCompassToCookies(
                data.hashedId,
                data.viewKey,
                data.title,
                true,
              );
            }
          });
      } else {
        this.newResearchCompassTitle = this.researchCompass.title;
        this.isEditingTitle = false;
      }
    },
    saveResearchCompassToCookies(hashedId, viewKey, title, updated = false) {
      let existingItems = this.$cookies.get("researchCompassItems") || [];
      const createdAt = new Date().toISOString();
      const updatedAt = new Date().toISOString();
      const item = { createdAt, updatedAt, hashedId, viewKey, title };
      const existingItem = existingItems.find((i) => i.hashedId === hashedId);
      if (existingItem) {
        item.createdAt = existingItem.createdAt;
        if (!updated) {
          item.updatedAt = existingItem.updatedAt;
        }
        existingItems = existingItems.filter((i) => i.hashedId !== hashedId);
      }
      existingItems.push(item);
      this.$cookies.set("researchCompassItems", JSON.stringify(existingItems), {
        path: "/",
        maxAge: 60 * 60 * 24 * 365,
      });
      this.$refs.chatHistory.updatePreviousResearchCompassItemsFromCookies();
    },
    getNameForQuestion(question) {
      let url = "";
      let api = null;
      if (this.$store.getters.person?.hashedId) {
        url = `/research-compass/name-for-question/`;
        api = this.$api.axios;
      } else {
        url = `/research-compass/name-for-question/`;
        api = this.$api.axiosPublic;
      }
      return api
        .get(
          url,
          keysToSnake({
            params: {
              question,
            },
          }),
        )
        .then((resp) => {
          const data = keysToCamel(resp.data);
          return data.name;
        });
    },
    adopt() {
      if (this.$store.getters.person && this.$route.params.viewKey) {
        this.$api.axios
          .post(
            `/research-compass/${this.$route.params.researchCompassId}/adopt/?view_key=${this.$route.params.viewKey}`,
          )
          .then((resp) => {
            const data = keysToCamel(resp.data);
            this.$refs.chatHistory.removeResearchCompassFromCookies(
              this.$route.params.researchCompassId,
            );
            this.$refs.chatHistory.getOwnResearchCompassItems();
            this.$router.push({
              name: "OwnResearchCompassDetail",
              params: {
                researchCompassId: data.hashedId,
              },
            });
          });
      }
    },
    selectSuggestion(example) {
      if (this.$refs.initialInput) {
        this.$refs.initialInput.selectSuggestion(example);
        this.$refs.initialInput.focusInput();
      }
    },
  },
};
</script>
<style lang="scss">
#headline-1 {
  text-align: center;
  font-family: "Poppins";
  font-size: 32px;
  font-style: normal;
  font-weight: 600;
  line-height: 48px;
  background: linear-gradient(88deg, #275258 -10.14%, #2dcfae 96.35%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
#headline-2 {
  text-align: center;
  font-family: "Poppins";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  max-width: 453px;
  color: #323232;
}
.center-holder {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}
.bottom-search-box-holder {
  box-shadow: 0 -10px 15px rgba(255, 255, 255, 0.7);
}
.dark {
  #headline-1 {
    background: linear-gradient(88deg, #e8f5b7 -10.14%, #2dcfae 96.35%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  #headline-2 {
    color: #d2d2d2;
  }
  .bottom-search-box-holder {
    box-shadow: 0 -10px 15px rgba(31, 69, 80, 0.7);
  }
}
</style>
